// ** React Imports
import { cloneElement, useEffect } from 'react'
import { NavLink, useLocation, useMatch } from 'react-router-dom'

// ** Third Party Components
import { Badge } from 'dsm-react-lib'
import classnames from 'classnames'

// ** Vertical Menu Array Of Items
import { useVerticalLayout } from '@navigation/vertical'

// ** Utils
import { search, getAllParents } from '@layouts/utils'

const VerticalNavMenuLink = ({
  item,
  groupActive,
  setGroupActive,
  activeItem,
  setActiveItem,
  groupOpen,
  setGroupOpen,
  toggleActiveGroup,
  parentItem,
  routerProps,
  currentActiveItem,
  menuCollapsed,
  menuHover
}) => {
  // ** Conditional Link Tag, if item has newTab or externalLink props use <a> tag else use NavLink
  const LinkTag = item.externalLink ? 'a' : NavLink
  const { verticalNavItems } = useVerticalLayout()

  // ** URL Vars
  const location = useLocation()
  const currentURL = location.pathname
  const hasNavLink = location.pathname.startsWith(item.navLink)
  const match = useMatch(item.navLink)
  const isActive = hasNavLink || item.navLink === activeItem

  // Props can't be reassigned, so resolve to a local variable instead of mutating currentActiveItem.
  const resolvedActiveItem =
    (match && match.pathname === item.navLink) || (match === null && hasNavLink)
      ? item.navLink
      : currentActiveItem
  const searchParents = (navigation, currentURL) => {
    const parents = search(navigation, currentURL, routerProps) // Search for parent object
    const allParents = getAllParents(parents, 'id') // Parents Object to Parents Array
    return allParents
  }

  // ** URL Vars
  const resetActiveGroup = (navLink) => {
    const parents = search(verticalNavItems, navLink, match)
    toggleActiveGroup(item.id, parents)
  }

  // ** Reset Active & Open Group Arrays
  const resetActiveAndOpenGroups = () => {
    setGroupActive([])
    setGroupOpen([])
  }

  // ** Checks url & updates active item
  useEffect(() => {
    if (resolvedActiveItem !== null) {
      setActiveItem(resolvedActiveItem)
      const arr = searchParents(verticalNavItems, currentURL)
      setGroupActive([...arr])
    }
  }, [location])

  return (
    <li
      className={`${item.tourClass} ${classnames({
        'nav-item': !item.children,
        disabled: item.disabled,
        active: isActive || null
      })}`}>
      <LinkTag
        data-testid={`leftNav-${item?.id}`}
        className={`!flex align-items-center !p-[8px] gap-[10px] transition-all duration-300 ease-in-out hover:!pl-[15px] ${
          isActive ? 'bg-primary' : ''
        }
          ${menuCollapsed && !menuHover ? 'menuCollapse justify-content-center' : ''}`}
        target={item.newTab ? '_blank' : undefined}
        {...(item.externalLink === true
          ? {
              href: item.navLink || '/'
            }
          : {
              to: item.navLink || '/',
              state: item.id === 'ali' ? { isNavClicked: true } : null // Pass state for tracking nav click
            })}
        onClick={(e) => {
          // Handle custom onClick first if provided
          if (item?.onClick && typeof item?.onClick === 'function') {
            e.preventDefault()
            item.onClick(e)
            return
          }

          if (!item.navLink.length) {
            e.preventDefault()
          }

          if (parentItem) {
            resetActiveGroup(item.navLink)
          } else {
            resetActiveAndOpenGroups()
          }
        }}>
        {cloneElement(item?.icon, {
          color: isActive ? 'white' : 'var(--text-body)'
        })}
        {(!menuCollapsed || menuHover) && (
          <span
            className={`menu-item text-truncate ${
              isActive ? 'text-[var(--white)]' : 'text-body-theme'
            }`}>
            {item?.title}
          </span>
        )}

        {item.badge && item.badgeText ? (
          <Badge className="ms-auto me-1" color={item.badge} type="pill" label={item.badgeText} />
        ) : null}
      </LinkTag>
    </li>
  )
}

export default VerticalNavMenuLink
